import { Formik } from "formik";
import * as Yup from "yup";
import { Form, Input, Button, Row, Col, Space, Select, Tooltip } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { CheckSquare, Edit3, Pencil, Trash2 } from "lucide-react";
import _ from "lodash";

const { TextArea } = Input;

// Field type options for clearance
const clearanceOptions = [
  { value: "text", label: "Text", disabled: false },
  { value: "checkbox", label: "Checkbox", disabled: false },
  { value: "radio", label: "Radio", disabled: false },
  { value: "number", label: "Number", disabled: false },
];

// Remark options
const remarkOptions = [
  { value: "text", label: "Text" },
  { value: "checkbox", label: "Checkbox" },
];

// Validation schema
const validationSchema = Yup.object().shape({
  clearance_type_id: Yup.string()
    .nullable()
    .required("Clearance type is required"),
  type: Yup.string().nullable().required("Expected value is required"),
  remark: Yup.string().nullable().required("Remark is required"),
  instruction: Yup.string().nullable().required("Instruction is required"),
  cleaning_type_id: Yup.array().when(["clearance_type_id", "type"], {
    is: (clearance_type_id, type) =>
      clearance_type_id && type?.toLowerCase() === "radio",
    then: () => Yup.array().min(1, "At least one cleaning option is required"),
    otherwise: () => Yup.array(),
  }),
});

const LineClearanceForm = ({
  editIndex,
  onSubmit = (values) => console.log("Submitted values:", values),
  clearance_type_list = [],
  cleaning_type_list = [],
  values = {
    clearance_type_id: null,
    type: null,
    remark: null,
    instruction: null,
    cleaning_type_id: [],
  },
}) => {
  return (
    <Formik
      initialValues={values}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {({ values, errors, touched, handleSubmit, setFieldValue }) => (
        <Form layout="vertical" onFinish={handleSubmit}>
          <Row gutter={24}>
            {/* Clearance Type */}
            <Col span={8}>
              <Form.Item
                label="Clearance Type"
                validateStatus={
                  touched.clearance_type_id && errors.clearance_type_id
                    ? "error"
                    : ""
                }
                help={touched.clearance_type_id && errors.clearance_type_id}
              >
                <Space.Compact style={{ width: "100%" }}>
                  <Select
                    placeholder="Select Clearance type"
                    value={values.clearance_type_id || null}
                    onChange={(val, option) => {
                      setFieldValue("clearance_type_id", val || "");
                      setFieldValue("type", "");
                      setFieldValue("cleaning_type_id", []);
                      if (val) {
                        setFieldValue("clearance_type", option.label);
                      }
                    }}
                    showSearch
                    allowClear
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    options={clearance_type_list?.map((item) => ({
                      value: item.clearance_type_id,
                      label: item.name,
                    }))}
                  />
                </Space.Compact>
              </Form.Item>
            </Col>

            {/* Expected Value */}
            <Col span={8}>
              <Form.Item
                label="Expected Value"
                validateStatus={touched.type && errors.type ? "error" : ""}
                help={touched.type && errors.type}
              >
                <Select
                  placeholder="Expected Value"
                  value={values.type || null}
                  onChange={(val) => {
                    setFieldValue("type", val || "");
                    setFieldValue("cleaning_type_id", []);
                  }}
                  showSearch
                  allowClear
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  options={clearanceOptions.map((option) => ({
                    value: option.value,
                    label: option.label,
                    disabled: option.disabled,
                  }))}
                />
              </Form.Item>
            </Col>

            {/* Remark */}
            <Col span={8}>
              <Form.Item
                label="Remark"
                validateStatus={touched.remark && errors.remark ? "error" : ""}
                help={touched.remark && errors.remark}
              >
                <Select
                  placeholder="Remark"
                  value={values.remark || null}
                  onChange={(val, option) => {
                    setFieldValue("remark", val || "");
                  }}
                  showSearch
                  allowClear
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  options={remarkOptions.map((option) => ({
                    value: option.value,
                    label: option.label,
                  }))}
                />
              </Form.Item>
            </Col>

            {/* Cleaning Type - conditional */}
            {values.clearance_type_id &&
              values.type?.toLowerCase() === "radio" && (
                <Col span={8}>
                  <Form.Item
                    label="Cleaning Type Options"
                    validateStatus={
                      touched.cleaning_type_id && errors.cleaning_type_id
                        ? "error"
                        : ""
                    }
                    help={touched.cleaning_type_id && errors.cleaning_type_id}
                  >
                    <Select
                      mode="multiple"
                      placeholder="Select cleaning types"
                      value={values.cleaning_type_id}
                      onChange={(val, option) => {
                        setFieldValue("cleaning_type_id", val);
                        if (val) {
                          setFieldValue(
                            "cleaning_type",
                            option.map((i) => i.label)
                          );
                        }
                      }}
                      showSearch
                      allowClear
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      options={cleaning_type_list?.map((item) => ({
                        value: item.id,
                        label: item.name,
                      }))}
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
              )}

            {/* Instruction */}
            <Col span={8}>
              <Form.Item
                label="Instruction"
                validateStatus={
                  touched.instruction && errors.instruction ? "error" : ""
                }
                help={touched.instruction && errors.instruction}
              >
                <TextArea
                  rows={3}
                  placeholder="Enter instruction"
                  value={values.instruction}
                  onChange={(e) => setFieldValue("instruction", e.target.value)}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Button type="primary" htmlType="submit" block>
                {editIndex !== null ? "Update" : "Add"}
              </Button>
            </Col>
          </Row>
        </Form>
      )}
    </Formik>
  );
};

export default LineClearanceForm;

export const LineClearanceCard = ({ fromView, item, onEdit, onDelete }) => {
  const {
    clearance_type,
    clearance_type_id,
    type,
    remark,
    instruction,
    cleaning_type,
  } = item;

  const showLabel = (label, id) => label || `${id ? "ID: " + id : "N/A"}`;

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition p-3 text-sm">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-yellow-100 rounded-md">
            <CheckSquare className="w-4 h-4 text-yellow-600" />
          </div>
          <p
            className="font-semibold text-gray-900 leading-tight truncate text-base w-64"
            title={showLabel(clearance_type, clearance_type_id)}
          >
            {showLabel(clearance_type, clearance_type_id)}
          </p>
        </div>

        {/* Actions */}
        {!fromView && (
          <div className="flex items-center gap-1">
            <button
              type="button"
              className="p-1.5 rounded-md hover:bg-blue-50 transition-colors border border-transparent hover:border-blue-200"
              onClick={() => onEdit()}
              title="Edit line clearance"
            >
              <Edit3 className="w-4 h-4 text-blue-600" />
            </button>
            <button
              type="button"
              className="p-1.5 rounded-md hover:bg-red-50 transition-colors border border-transparent hover:border-red-200"
              onClick={() => onDelete()}
              title="Delete line clearance"
            >
              <Trash2 className="w-4 h-4 text-red-500" />
            </button>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="grid grid-cols-2 gap-3">
        {type && (
          <div className="bg-indigo-50 rounded-md p-2.5">
            <p className="text-xs text-indigo-700 font-medium mb-1">Type</p>
            <p className="text-indigo-900 font-semibold truncate" title={type}>
              {_.capitalize(type)}
            </p>
          </div>
        )}

        {remark && (
          <div className="bg-purple-50 rounded-md p-2.5">
            <p className="text-xs text-purple-700 font-medium mb-1">Remark</p>
            <p
              className="text-purple-900 font-semibold truncate"
              title={remark}
            >
              {_.capitalize(remark)}
            </p>
          </div>
        )}
      </div>

      {/* Instruction */}
      {instruction && (
        <div className="mt-3 bg-gray-50 rounded-md p-2.5">
          <p className="text-xs text-gray-600 font-medium mb-1">Instruction</p>
          <p className="text-gray-800 text-sm leading-relaxed">{instruction}</p>
        </div>
      )}

      {/* Cleaning Types */}
      {cleaning_type && cleaning_type.length > 0 && (
        <div className="mt-3 bg-green-50 rounded-md p-2.5">
          <p className="text-xs text-green-700 font-medium mb-1">
            Cleaning Types
          </p>
          <div className="flex flex-wrap gap-1">
            {cleaning_type.map((type, idx) => (
              <span
                key={idx}
                className="inline-block bg-green-100 text-green-800 px-2 py-0.5 rounded text-xs font-medium truncate"
                title={type.trim()}
              >
                {type.trim()}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
